import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHeart, FaBone, FaLeaf, FaStar, FaShoppingCart } from 'react-icons/fa';
import { GiMilkCarton, GiCow } from 'react-icons/gi';
import { useCart } from '../../context/CartContext';
import { products } from '../../data/products';
import DecorativeHeading, { SectionHeading } from '../../components/DecorativeHeading/DecorativeHeading';
import styles from './Home.module.css';
import bannerImage from '../../assets/banner.jpg';
import bannerCow from '../../assets/banner-cow.png';
import bannerChurning from '../../assets/banner-churning.png';
import bannerSunrise from '../../assets/banner-sunrise.png';
import cowImage from '../../assets/cow.jpg';
import heroDecoration from '../../assets/hero-decoration.png';
import farmingScene from '../../assets/farming-scene.png';
import gheeProcess from '../../assets/ghee-process.png';
import testimonialsDivider from '../../assets/testimonials-divider.png';
import featuresDivider from '../../assets/features-divider.png';
import img1 from "../../assets/New-Images/img1.JPG";
import img2 from "../../assets/New-Images/img2.JPG";
import img3 from "../../assets/wp-images/img5.jpeg";
import newImg8 from "../../assets/New-Images/img8.jpg";
import newImg16 from "../../assets/New-Images/img16.jpg";
import wpImg9 from "../../assets/wp-images/img9.jpeg";
import aboutVideo from "../../assets/about.mp4";


const heroSlides = [
  {
    image: img1,
    title: "Pure Gir Cow Bilona Ghee",
    gujaratiTitle: "શુદ્ધ ગીર ગાયનું બિલોણા ઘી",
    subtitle: "Rooted in Tradition, Delivered with Trust",
    gujaratiSubtitle: "પરંપરામાં મૂળ, વિશ્વાસ સાથે વિતરિત"
  },
  {
    image: img2,
    title: "Vedic Bilona Method",
    gujaratiTitle: "વૈદિક બિલોણા પદ્ધતિ",
    subtitle: "Hand-churned for Authentic Taste & Aroma",
    gujaratiSubtitle: "અસલ સ્વાદ અને સુગંધ માટે હાથથી બનાવેલ"
  },
  {
    image: img3,
    title: "Straight from Our Farm",
    gujaratiTitle: "સીધું અમારા ખેતરમાંથી",
    subtitle: "100% Natural, Chemical Free & Organic",
    gujaratiSubtitle: "૧૦૦% કુદરતી, કેમિકલ મુક્ત અને ઓર્ગેનિક"
  }
];

const Home = () => {
  const { addToCart } = useCart();
  const [currentSlide, setCurrentSlide] = React.useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const benefits = [
    { icon: <FaHeart />, title: 'Heart Health', desc: 'Rich in Omega-3' },
    { icon: <FaBone />, title: 'Strong Bones', desc: 'High in Vitamin K2' },
    { icon: <FaLeaf />, title: 'Better Digestion', desc: 'Butyric Acid' },
  ];

  const features = [
    { icon: '🌿', title: '100% Natural', desc: 'Chemical Free', gujarati: 'શુદ્ધ કુદરતી' },
    { icon: '🥄', title: 'Traditional Bilona', desc: 'Hand-Churned', gujarati: 'પરંપરાગત બિલોણા' },
    { icon: '🐄', title: 'Pure Gir Cow', desc: 'Milk', gujarati: 'ગીર ગાયનું દૂધ' },
    { icon: '🏡', title: 'Farm Fresh', desc: 'Direct to Home', gujarati: 'ફાર્મથી ઘર સુધી' },
  ];

  const testimonials = [
    {
      name: 'Ramesh Patel',
      location: 'Ahmedabad',
      text: 'આજે અસલી સ્વાદ મળ્યો! Best ghee for my family.',
      rating: 5,
    },
    {
      name: 'Priya Shah',
      location: 'Surat',
      text: 'Pure and authentic! મારા બાળકોને ખૂબ ગમે છે.',
      rating: 5,
    },
    {
      name: 'Kiran Desai',
      location: 'Vadodara',
      text: 'Traditional taste like my grandmother made. આભાર!',
      rating: 5,
    },
  ];

  return (
    <div className={styles.home}>
      {/* Hero Section Slider */}
      <section className={styles.heroSlider}>
        <AnimatePresence mode='wait'>
          <motion.div
            key={currentSlide}
            className={styles.slideBackground}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            style={{ backgroundImage: `url(${heroSlides[currentSlide].image})` }}
          />
        </AnimatePresence>

        <div className={styles.heroOverlay}></div>

        {/* Floating Leaves */}
        <div className={styles.floatingLeaves}>
          <span className={styles.leaf}>🍃</span>
          <span className={styles.leaf}>🍃</span>
          <span className={styles.leaf}>🍃</span>
          <span className={styles.leaf}>🍃</span>
        </div>

        <div className={styles.heroContentContainer}>
          <AnimatePresence mode='wait'>
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.8 }}
              className={styles.heroTextContent}
            >
              <h1 className={styles.heroTitle}>
                {heroSlides[currentSlide].title}
                <br />
                <span className={`${styles.gujaratiTitle} gujarati-text`}>
                  {heroSlides[currentSlide].gujaratiTitle}
                </span>
              </h1>

              <p className={styles.heroTagline}>
                {heroSlides[currentSlide].subtitle}
                <br />
                <span className="gujarati-text">
                  {heroSlides[currentSlide].gujaratiSubtitle}
                </span>
              </p>

              <div className={styles.heroCta}>
                <Link to="/products">
                  <button className="btn-primary">Order Now</button>
                </Link>
                <Link to="/process">
                  <button className="btn-secondary">Know Our Process</button>
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>


        </div>

        {/* Certification Badges */}
        <div className={styles.badges}>
          <div className={styles.badge}>
            <span className={styles.badgeIcon}>✓</span>
            <span>100% Natural</span>
          </div>
          <div className={styles.badge}>
            <span className={styles.badgeIcon}>✓</span>
            <span>Chemical Free</span>
          </div>
          {/* <div className={styles.badge}>
            <span className={styles.badgeIcon}>✓</span>
            <span>Full oProtein</span>
          </div> */}
          <div className={styles.badge}>
            <span className={styles.badgeIcon}>✓</span>
            <span>Hand-Churned</span>
          </div>
        </div>
      </section>

      {/* Decorative Section Divider */}


      {/* Products Showcase Section */}
      <section className={styles.productsSection}>
        <div className="container">
          <DecorativeHeading variant="gold" gujaratiText="અમારું પ્રીમિયમ બિલોણા ઘી">
            Our Premium Bilona Ghee
          </DecorativeHeading>

          <div className={styles.productsGrid}>
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                className={styles.productCard}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                {product.popular && (
                  <div className={styles.popularBadge}>
                    <FaStar /> Most Popular
                  </div>
                )}

                <div className={styles.productImageContainer}>
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className={styles.productImage}
                  />
                </div>

                <div className={styles.productInfo}>
                  <h3 className={styles.productName}>{product.name}</h3>
                  <p className={styles.productGujarati}>{product.gujarati}</p>

                  <div className={styles.productFeatures}>
                    {product.features.slice(0, 2).map((feature, idx) => (
                      <span key={idx} className={styles.feature}>
                        <GiMilkCarton /> {feature}
                      </span>
                    ))}
                  </div>

                  <div className={styles.productFooter}>
                    <p className={styles.productPrice}>₹{product.price}</p>
                    <button
                      className={styles.addToCartBtn}
                      onClick={() => {
                        addToCart(product, 1);
                      }}
                    >
                      <FaShoppingCart /> Add to Cart
                    </button>
                  </div>

                  <Link to={`/products/${product.id}`} className={styles.viewDetailsLink}>
                    View Details →
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div className={styles.sectionDivider}>
        <img src={wpImg9} alt="Farm landscape" />
      </div>

      {/* About Bilona Ghee Section */}
      <section className={`${styles.aboutSection} texture-bg`}>
        <div className="container">
          <DecorativeHeading gujaratiText="બિલોણા ઘી વિશે">
            About Bilona Ghee
          </DecorativeHeading>

          <div className={styles.aboutContent}>
            <div className={styles.aboutVideoContainer}>
              <video
                className={styles.aboutVideo}
                controls
                autoPlay
                muted
                loop
                playsInline
              >
                <source src={aboutVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>

            <div className={styles.aboutText}>
              <p>
                Bilona Ghee is made using the ancient Vedic method where curd is hand-churned
                using a wooden churner (bilona) to extract butter, which is then heated to make pure ghee.
              </p>
              <p className="gujarati-text">
                બિલોણા ઘી પ્રાચીન વૈદિક પદ્ધતિથી બનાવવામાં આવે છે.
              </p>

              <div className={styles.benefits}>
                {benefits.map((benefit, index) => (
                  <div key={index} className={styles.benefitCard}>
                    <div className={styles.benefitIcon}>{benefit.icon}</div>
                    <h4>{benefit.title}</h4>
                    <p>{benefit.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Farm Divider */}
      <div className={styles.sectionDivider}>
        <img src={newImg16} alt="Farm scene" />
      </div>

      {/* Why Choose Us Section */}
      <section className={styles.featuresSection}>
        <div className="container">
          <DecorativeHeading gujaratiText="અમને કેમ પસંદ કરો">
            Why Choose Us
          </DecorativeHeading>

          <div className={styles.processIllustration}>
            <img src={gheeProcess} alt="Traditional Bilona Process" />
          </div>

          <div className={styles.featuresGrid}>
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className={styles.featureCard}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <div className={styles.featureIcon}>{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
                <p className={`${styles.featureGujarati} gujarati-text`}>{feature.gujarati}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cow Grazing Divider */}
      {/* <div className={styles.sectionDivider}>
        <img src={testimonialsDivider} alt="Farm decoration" />
      </div> */}

      {/* Testimonials Section */}
      <section className={`${styles.testimonialsSection} texture-bg`}>
        <div className="container">
          <DecorativeHeading variant="gold" gujaratiText="અમારા ગ્રાહકો શું કહે છે">
            What Our Customers Say
          </DecorativeHeading>

          <div className={styles.testimonialsGrid}>
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className={styles.testimonialCard}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={styles.stars}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                </div>
                <p className={styles.testimonialText}>{testimonial.text}</p>
                <div className={styles.testimonialAuthor}>
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.location}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaPatternOverlay}></div>
        <div className={styles.floatingShapes}>
          <span className={styles.shape}></span>
          <span className={styles.shape}></span>
          <span className={styles.shape}></span>
        </div>

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className={styles.ctaContentCard}>
            <DecorativeHeading variant="gold" gujaratiText="શુદ્ધ પરંપરાનો અનુભવ કરો">
              Experience Pure Tradition
            </DecorativeHeading>

            <p className={styles.ctaDescription}>
              Join thousands of happy families who trust Bahariya Farm for their daily nutrition.
            </p>

            <Link to="/products">
              <button className={`btn-primary ${styles.ctaButton}`}>
                Order Now <span className={styles.btnIcon}>➜</span>
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
